// src/services/laporan.ts
import api from './api';
import { LaporanMasalah, PaginatedResponse, AvailableTransition, StateTransitionLog } from '../types';

export interface LaporanFilter {
  status?: string;
  kategori?: number;
  unit?: number;
  periode?: number;
  risiko?: string;
  search?: string;
  page?: number;
  per_page?: number;
}

export const laporanService = {
  getAll: async (filters?: LaporanFilter) => {
    const res = await api.get('/laporan', { params: filters });
    return res.data as PaginatedResponse<LaporanMasalah>;
  },
  getById: async (id: number) => {
    const res = await api.get(`/laporan/${id}`);
    return res.data as LaporanMasalah;
  },
  create: async (data: Partial<LaporanMasalah>) => {
    const res = await api.post('/laporan', data);
    return res.data as LaporanMasalah;
  },
  update: async (id: number, data: Partial<LaporanMasalah>) => {
    const res = await api.put(`/laporan/${id}`, data);
    return res.data as LaporanMasalah;
  },
  delete: async (id: number) => {
    await api.delete(`/laporan/${id}`);
  },
  submit: async (id: number) => {
    const res = await api.post(`/laporan/${id}/submit`);
    return res.data;
  },

  // === STATE MACHINE ===

  getTransitions: async (id: number): Promise<AvailableTransition[]> => {
    const res = await api.get(`/laporan/${id}/transitions`);
    return (res.data?.data || res.data) as AvailableTransition[];
  },

  getStateHistory: async (id: number): Promise<StateTransitionLog[]> => {
    const res = await api.get(`/laporan/${id}/state-history`);
    return (res.data?.data || res.data) as StateTransitionLog[];
  },

  changeStatus: async (id: number, status: string, notes?: string): Promise<LaporanMasalah> => {
    const res = await api.post(`/laporan/${id}/status`, { status, notes });
    return (res.data?.data || res.data) as LaporanMasalah;
  },

  // === COMMENTS & ATTACHMENTS ===

  addComment: async (id: number, content: string, parent_id?: number) => {
    const res = await api.post(`/laporan/${id}/comment`, { content, parent_id });
    return res.data;
  },
  uploadAttachment: async (id: number, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    const res = await api.post(`/laporan/${id}/attachment`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return res.data;
  },
  downloadAttachment: async (laporanId: number, attachmentId: number, filename: string) => {
    try {
      const res = await api.get(`/laporan/${laporanId}/attachment/${attachmentId}`, {
        responseType: 'arraybuffer',
      });

      const contentType = res.headers['content-type'];
      if (contentType && contentType.includes('application/json')) {
        const errorData = JSON.parse(new TextDecoder().decode(res.data));
        throw new Error(errorData.message || 'Download failed');
      }

      const blob = new Blob([res.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      console.error('Download attachment error:', error);
      throw error;
    }
  },
  deleteAttachment: async (laporanId: number, attachmentId: number) => {
    await api.delete(`/laporan/${laporanId}/attachment/${attachmentId}`);
  },
};